import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { getTeam, getBlogs, getClients, getAppointments, getContacts } from '@/lib/api';
import { Link } from 'react-router-dom';
import { Users, FileText, Building2, Calendar, MessageSquare, TrendingUp, ArrowRight } from 'lucide-react';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    team: 0,
    blogs: 0,
    clients: 0,
    appointments: 0,
    contacts: 0,
  });
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [teamRes, blogRes, clientRes, aptRes, contactRes] = await Promise.all([
        getTeam(),
        getBlogs(false),
        getClients(),
        getAppointments(),
        getContacts(),
      ]);

      setStats({
        team: teamRes.data.length,
        blogs: blogRes.data.length,
        clients: clientRes.data.length,
        appointments: aptRes.data.length,
        contacts: contactRes.data.length,
      });

      setRecentAppointments(aptRes.data.slice(0, 5));
      setRecentContacts(contactRes.data.filter(c => !c.read).slice(0, 5));
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const statCards = [
    { name: 'Team Members', value: stats.team, icon: Users, link: '/admin/team', color: 'text-blue-500' },
    { name: 'Blog Posts', value: stats.blogs, icon: FileText, link: '/admin/blogs', color: 'text-green-500' },
    { name: 'Clients', value: stats.clients, icon: Building2, link: '/admin/clients', color: 'text-purple-500' },
    { name: 'Appointments', value: stats.appointments, icon: Calendar, link: '/admin/appointments', color: 'text-[#ff5a1f]' },
    { name: 'Messages', value: stats.contacts, icon: MessageSquare, link: '/admin/contacts', color: 'text-yellow-500' },
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {statCards.map((stat) => (
            <Link
              key={stat.name}
              to={stat.link}
              className="bg-[#0A0A0A] border border-[#1F1F1F] p-6 hover:border-[#ff5a1f]/50 transition-all group"
              data-testid={`stat-card-${stat.name.toLowerCase().replace(' ', '-')}`}
            >
              <div className="flex items-center justify-between mb-4">
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
                <TrendingUp className="w-4 h-4 text-[#333] group-hover:text-[#ff5a1f] transition-colors" />
              </div>
              <p className="font-syne text-3xl font-bold text-white mb-1">{stat.value}</p>
              <p className="text-[#888] text-sm">{stat.name}</p>
            </Link>
          ))}
        </div>

        {/* Recent Activity */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Recent Appointments */}
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-syne font-bold text-white">Recent Appointments</h2>
              <Link
                to="/admin/appointments"
                className="text-[#ff5a1f] text-sm flex items-center gap-1 hover:gap-2 transition-all"
              >
                View All
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            {recentAppointments.length > 0 ? (
              <div className="space-y-4">
                {recentAppointments.map((apt) => (
                  <div
                    key={apt.id}
                    className="flex items-center justify-between py-3 border-b border-[#1F1F1F] last:border-0"
                  >
                    <div>
                      <p className="text-white font-medium">{apt.name}</p>
                      <p className="text-[#888] text-sm">{apt.service}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#888] text-sm">{apt.date}</p>
                      <span className={`text-xs px-2 py-1 ${
                        apt.status === 'confirmed' ? 'bg-green-500/10 text-green-500' :
                        apt.status === 'pending' ? 'bg-yellow-500/10 text-yellow-500' :
                        'bg-red-500/10 text-red-500'
                      }`}>
                        {apt.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#555] text-center py-8">No appointments yet</p>
            )}
          </div>

          {/* Unread Messages */}
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-syne font-bold text-white">
                Unread Messages
                {recentContacts.length > 0 && (
                  <span className="ml-2 bg-[#ff5a1f] text-white text-xs px-2 py-1 rounded-full">
                    {recentContacts.length}
                  </span>
                )}
              </h2>
              <Link
                to="/admin/contacts"
                className="text-[#ff5a1f] text-sm flex items-center gap-1 hover:gap-2 transition-all"
              >
                View All
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            {recentContacts.length > 0 ? (
              <div className="space-y-4">
                {recentContacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="py-3 border-b border-[#1F1F1F] last:border-0"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-white font-medium">{contact.name}</p>
                      <span className="w-2 h-2 bg-[#ff5a1f] rounded-full" />
                    </div>
                    <p className="text-[#888] text-sm truncate">{contact.subject}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#555] text-center py-8">No unread messages</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-6">
          <h2 className="font-syne font-bold text-white mb-6">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link
              to="/admin/team"
              className="flex items-center gap-3 p-4 bg-[#121212] hover:bg-[#ff5a1f]/10 hover:border-[#ff5a1f] border border-[#1F1F1F] transition-all"
            >
              <Users className="w-5 h-5 text-[#ff5a1f]" />
              <span className="text-white text-sm">Add Team Member</span>
            </Link>
            <Link
              to="/admin/blogs"
              className="flex items-center gap-3 p-4 bg-[#121212] hover:bg-[#ff5a1f]/10 hover:border-[#ff5a1f] border border-[#1F1F1F] transition-all"
            >
              <FileText className="w-5 h-5 text-[#ff5a1f]" />
              <span className="text-white text-sm">Write Blog Post</span>
            </Link>
            <Link
              to="/admin/clients"
              className="flex items-center gap-3 p-4 bg-[#121212] hover:bg-[#ff5a1f]/10 hover:border-[#ff5a1f] border border-[#1F1F1F] transition-all"
            >
              <Building2 className="w-5 h-5 text-[#ff5a1f]" />
              <span className="text-white text-sm">Add Client Logo</span>
            </Link>
            <Link
              to="/admin/faq"
              className="flex items-center gap-3 p-4 bg-[#121212] hover:bg-[#ff5a1f]/10 hover:border-[#ff5a1f] border border-[#1F1F1F] transition-all"
            >
              <MessageSquare className="w-5 h-5 text-[#ff5a1f]" />
              <span className="text-white text-sm">Add FAQ</span>
            </Link>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
